import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Loader2, RefreshCw } from "lucide-react";
import {
  fetchDashboardAnalytics,
  fetchUserAnalytics,
  fetchEventAnalytics,
  clearAnalyticsError,
} from "../redux/slices/analyticsSlice";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatDate } from "../lib/utils";

const CHART_COLORS = [
  "hsl(var(--primary))",
  "#22c55e",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
];

function formatDateInput(d) {
  return d.toISOString().slice(0, 10);
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { start: formatDateInput(start), end: formatDateInput(end) };
}

function toCount(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseAggregateRows(rows, labelKey) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, i) => ({
    name: String(
      row[labelKey] ?? row.get?.(labelKey) ?? row.dataValues?.[labelKey] ?? "?"
    ),
    value: toCount(row.count ?? row.get?.("count") ?? row.dataValues?.count),
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));
}

function parseTimeSeries(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const raw =
      row.date ?? row.get?.("date") ?? row.dataValues?.date ?? row.created_at;
    return {
      date: raw ? formatDate(raw) : "?",
      count: toCount(row.count ?? row.get?.("count") ?? row.dataValues?.count),
    };
  });
}

function ChartCard({ title, description, height = 280, empty, loading, children }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent style={{ height }}>
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : empty ? (
          <p className="text-sm text-muted-foreground text-center py-12">
            No data for this range.
          </p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

function Analytics() {
  const dispatch = useDispatch();
  const { dashboard, userAnalytics, eventAnalytics, loading, error } =
    useSelector((state) => state.analytics);

  const initialRange = useMemo(() => defaultDateRange(), []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);

  const dateParams = useMemo(
    () => ({ start_date: startDate, end_date: endDate }),
    [startDate, endDate]
  );

  const loadAll = () => {
    dispatch(clearAnalyticsError());
    dispatch(fetchDashboardAnalytics(dateParams));
    dispatch(fetchUserAnalytics(dateParams));
    dispatch(fetchEventAnalytics(dateParams));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, startDate, endDate]);

  const overview = dashboard?.overview || {};
  const topEventsData = useMemo(
    () => parseAggregateRows(dashboard?.topEvents, "event_name"),
    [dashboard]
  );
  const platformData = useMemo(
    () => parseAggregateRows(dashboard?.eventsByPlatform, "platform"),
    [dashboard]
  );
  const userGrowthData = useMemo(
    () => parseTimeSeries(userAnalytics?.userGrowth),
    [userAnalytics]
  );
  const usersByRoleData = useMemo(
    () => parseAggregateRows(userAnalytics?.usersByRole, "role"),
    [userAnalytics]
  );
  const eventsOverTimeData = useMemo(
    () => parseTimeSeries(eventAnalytics?.eventsOverTime),
    [eventAnalytics]
  );
  const eventsByCategoryData = useMemo(
    () =>
      parseAggregateRows(eventAnalytics?.eventsByCategory, "event_category"),
    [eventAnalytics]
  );

  const applyPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(formatDateInput(start));
    setEndDate(formatDateInput(end));
  };

  const busy = loading && !dashboard;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics</h1>
          <p className="text-muted-foreground">
            User growth and app events for the selected date range.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <p className="text-sm text-destructive">
          {error} — ensure you are logged in as admin.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Date range</CardTitle>
          <CardDescription>Filters registrations and event metrics.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 items-end">
          <div className="grid gap-2">
            <Label htmlFor="start-date">From</Label>
            <Input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="end-date">To</Label>
            <Input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2 pb-1">
            <Button type="button" variant="secondary" size="sm" onClick={() => applyPreset(7)}>
              7d
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => applyPreset(30)}>
              30d
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => applyPreset(90)}>
              90d
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[
          ["Total users", overview.totalUsers, "All time"],
          ["Active (30d)", overview.activeUsers, "Recent login"],
          ["New users", overview.newUsers, "In range"],
          ["Events", overview.totalEvents, "In range"],
        ].map(([label, val, hint]) => (
          <Card key={label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{busy ? "…" : toCount(val)}</div>
              <p className="text-xs text-muted-foreground mt-1">{hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Top events" description="By count" empty={!topEventsData.length} loading={busy}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topEventsData} layout="vertical" margin={{ left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                {topEventsData.map((e, i) => (
                  <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="By platform" empty={!platformData.length} loading={busy}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={platformData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {platformData.map((e, i) => (
                  <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="User sign-ups" description="Daily" empty={!userGrowthData.length} loading={busy}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" name="Users" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Users by role" empty={!usersByRoleData.length} loading={busy}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={usersByRoleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {usersByRoleData.map((e, i) => (
                  <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Events over time" empty={!eventsOverTimeData.length} loading={busy}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={eventsOverTimeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" name="Events" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Events by category" empty={!eventsByCategoryData.length} loading={busy}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={eventsByCategoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" name="Events" radius={[4, 4, 0, 0]}>
                {eventsByCategoryData.map((e, i) => (
                  <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

export default Analytics;